import React, { useEffect, useState } from 'react';
import { Table, Input, Select, Space, message, Card, Tag, Button, Popconfirm, DatePicker } from 'antd';
import { DownloadOutlined, CheckCircleOutlined, UndoOutlined, PrinterOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { getAllCertificates, downloadCertificate, deliverCertificate, undoDeliverCertificate, markCertificatePrinted, undoCertificatePrinted } from '../api/certificates';
import useDebounce from '../hooks/useDebounce';
import usePersistedPageSize from '../hooks/usePersistedPageSize';
import useServerSort from '../hooks/useServerSort';
import useAuthStore from '../store/authStore';
import { apiErrorMessage } from '../utils/apiError';

const { Option } = Select;
const { RangePicker } = DatePicker;

const CAN_UNDO_ROLES = ['developer_admin', 'super_admin', 'branch_manager'];
const CAN_VIEW_USER_DETAIL_ROLES = ['developer_admin', 'super_admin', 'branch_manager'];

const CertificateDeliveries = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const canUndo = CAN_UNDO_ROLES.includes(user?.role);
  const canViewUserDetail = CAN_VIEW_USER_DETAIL_ROLES.includes(user?.role);

  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [deliveredFilter, setDeliveredFilter] = useState(null);
  // Basim/teslim birlesik durum filtresi. Ayri bir 'printed' filtresi yerine
  // birlesik: listenin asil ihtiyaci "basildi ama teslim edilmedi".
  const [statusFilter, setStatusFilter] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const debouncedSearch = useDebounce(search, 400);
  const [persistedPageSize, setPersistedPageSize] = usePersistedPageSize('certificate-deliveries', 20);
  const [pagination, setPagination] = useState({ current: 1, pageSize: persistedPageSize, total: 0 });
  const { sortField, sortOrder, applySorter } = useServerSort();

  const fetchCertificates = async (page = 1, searchText = debouncedSearch, delivered = deliveredFilter, perPage = pagination.pageSize, sortBy = sortField, sortDir = sortOrder ? (sortOrder === 'descend' ? 'desc' : 'asc') : null, range = dateRange) => {
    setLoading(true);
    try {
      const response = await getAllCertificates({
        page,
        per_page: perPage,
        search: searchText,
        delivered,
        // Parametre listesi zaten uzun; bunu pozisyonel eklemek yerine
        // state'ten okuyoruz - fonksiyon her render'da yeniden tanimlandigi
        // icin guncel degeri goruyor, effect de degisimde tetikliyor.
        status: statusFilter || undefined,
        date_from: range?.[0] ? range[0].format('YYYY-MM-DD') : undefined,
        date_to: range?.[1] ? range[1].format('YYYY-MM-DD') : undefined,
        sort_by: sortBy || undefined,
        sort_direction: sortDir || undefined,
      });
      setCertificates(response.data.data);
      setPagination({
        current: response.data.current_page,
        pageSize: response.data.per_page,
        total: response.data.total,
      });
    } catch (error) {
      message.error('Sertifikalar yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCertificates(1, debouncedSearch, deliveredFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, statusFilter]);

  const handleDownload = async (cert) => {
    setActionLoading(prev => ({ ...prev, [cert.id]: true }));
    try {
      const res = await downloadCertificate(cert.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${cert.certificate_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      message.error('Sertifika indirilemedi.');
    } finally {
      setActionLoading(prev => ({ ...prev, [cert.id]: false }));
    }
  };

  const handleDeliver = async (cert) => {
    setActionLoading(prev => ({ ...prev, [cert.id]: true }));
    try {
      await deliverCertificate(cert.id);
      message.success('Sertifika teslim edildi olarak işaretlendi.');
      fetchCertificates(pagination.current, debouncedSearch, deliveredFilter, pagination.pageSize);
    } catch (error) {
      message.error(apiErrorMessage(error, 'İşlem başarısız.'));
    } finally {
      setActionLoading(prev => ({ ...prev, [cert.id]: false }));
    }
  };

  // Basim, teslimden AYRI: haftalik toplu basiliyor, teslim sonraki hafta
  // tek tek yapiliyor. Ikisi tek alanda tutulunca basilmis sertifika tekrar
  // basiliyordu.
  const handlePrinted = async (cert) => {
    setActionLoading(prev => ({ ...prev, [cert.id]: true }));
    try {
      await markCertificatePrinted(cert.id);
      message.success('Sertifika basıldı olarak işaretlendi.');
      fetchCertificates(pagination.current, debouncedSearch, deliveredFilter, pagination.pageSize);
    } catch (error) {
      message.error(apiErrorMessage(error, 'İşlem başarısız.'));
    } finally {
      setActionLoading(prev => ({ ...prev, [cert.id]: false }));
    }
  };

  const handleUndoPrinted = async (cert) => {
    setActionLoading(prev => ({ ...prev, [cert.id]: true }));
    try {
      await undoCertificatePrinted(cert.id);
      message.success('Basıldı işareti geri alındı.');
      fetchCertificates(pagination.current, debouncedSearch, deliveredFilter, pagination.pageSize);
    } catch (error) {
      message.error(apiErrorMessage(error, 'İşlem başarısız.'));
    } finally {
      setActionLoading(prev => ({ ...prev, [cert.id]: false }));
    }
  };

  const handleUndo = async (cert) => {
    setActionLoading(prev => ({ ...prev, [cert.id]: true }));
    try {
      await undoDeliverCertificate(cert.id);
      message.success('Teslim işareti geri alındı.');
      fetchCertificates(pagination.current, debouncedSearch, deliveredFilter, pagination.pageSize);
    } catch (error) {
      message.error(apiErrorMessage(error, 'İşlem başarısız.'));
    } finally {
      setActionLoading(prev => ({ ...prev, [cert.id]: false }));
    }
  };

  const columns = [
    {
      title: 'Öğrenci',
      key: 'student',
      render: (_, record) => record.student ? (
        <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => navigate(`/students/${record.student.id}`)}>
          {record.student.name} {record.student.surname}
        </Button>
      ) : '—',
    },
    {
      title: 'Veli',
      key: 'parents',
      render: (_, record) => {
        const ps = record.student?.parents || [];
        if (!ps.length) return <span className="text-gray-400">—</span>;
        if (!canViewUserDetail) return ps.map(p => `${p.name} ${p.surname}`).join(', ');
        return ps.map((p, i) => (
          <React.Fragment key={p.id}>
            {i > 0 && ', '}
            <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => navigate(`/users/${p.id}`)}>
              {p.name} {p.surname}
            </Button>
          </React.Fragment>
        ));
      },
    },
    {
      title: 'Sertifika Programı',
      key: 'course',
      render: (_, record) => record.course?.name || '—',
    },
    {
      title: 'Sertifika No',
      dataIndex: 'certificate_number',
      key: 'certificate_number',
      sorter: true,
      sortOrder: sortField === 'certificate_number' ? sortOrder : null,
    },
    {
      title: 'Oluşturulma Tarihi',
      key: 'issued_at',
      sorter: true,
      sortOrder: sortField === 'issued_at' ? sortOrder : null,
      render: (_, record) => record.issued_at ? dayjs(record.issued_at).format('DD.MM.YYYY') : '—',
    },
    {
      title: 'Basım Durumu',
      key: 'printed_at',
      sorter: true,
      sortOrder: sortField === 'printed_at' ? sortOrder : null,
      render: (_, record) => {
        if (!record.printed_at) {
          return <Tag color="default">Basılmadı</Tag>;
        }
        return (
          <Tag color="blue">
            Basıldı ({dayjs(record.printed_at).format('DD.MM.YYYY')}
            {record.printed_by ? (
              <>
                {' — '}
                {canViewUserDetail ? (
                  <span
                    className="hover:underline cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); navigate(`/users/${record.printed_by.id}`); }}
                  >
                    {record.printed_by.name} {record.printed_by.surname}
                  </span>
                ) : `${record.printed_by.name} ${record.printed_by.surname}`}
              </>
            ) : ''})
          </Tag>
        );
      },
    },
    {
      title: 'Teslim Durumu',
      key: 'delivered_at',
      sorter: true,
      sortOrder: sortField === 'delivered_at' ? sortOrder : null,
      render: (_, record) => {
        if (!record.delivered_at) {
          return <Tag color="orange">Bekliyor</Tag>;
        }
        return (
          <Tag color="green">
            Teslim Edildi ({dayjs(record.delivered_at).format('DD.MM.YYYY')}
            {record.delivered_by ? (
              <>
                {' — '}
                {canViewUserDetail ? (
                  <span
                    className="hover:underline cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); navigate(`/users/${record.delivered_by.id}`); }}
                  >
                    {record.delivered_by.name} {record.delivered_by.surname}
                  </span>
                ) : `${record.delivered_by.name} ${record.delivered_by.surname}`}
              </>
            ) : ''})
          </Tag>
        );
      },
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {!record.pdf_path && <Tag color="orange">PDF hazırlanıyor</Tag>}
          {record.pdf_path && (
            <Button
              size="small"
              icon={<DownloadOutlined />}
              loading={actionLoading[record.id]}
              onClick={() => handleDownload(record)}
            >
              İndir
            </Button>
          )}
          {!record.printed_at ? (
            <Button
              size="small"
              icon={<PrinterOutlined />}
              loading={actionLoading[record.id]}
              disabled={!record.pdf_path}
              onClick={() => handlePrinted(record)}
            >
              Sertifika Basıldı
            </Button>
          ) : canUndo && (
            <Popconfirm
              title="Basıldı işaretini geri almak istediğinize emin misiniz?"
              onConfirm={() => handleUndoPrinted(record)}
              okText="Geri Al"
              cancelText="İptal"
            >
              <Button size="small" icon={<UndoOutlined />} loading={actionLoading[record.id]}>
                Basımı Geri Al
              </Button>
            </Popconfirm>
          )}
          {!record.delivered_at ? (
            <Button
              type="primary"
              size="small"
              icon={<CheckCircleOutlined />}
              loading={actionLoading[record.id]}
              disabled={!record.pdf_path}
              onClick={() => handleDeliver(record)}
            >
              Teslim Et
            </Button>
          ) : canUndo && (
            <Popconfirm
              title="Teslim işaretini geri almak istediğinize emin misiniz?"
              onConfirm={() => handleUndo(record)}
              okText="Geri Al"
              cancelText="İptal"
            >
              <Button size="small" icon={<UndoOutlined />} loading={actionLoading[record.id]}>
                Teslimi Geri Al
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Sertifika Teslim Takibi</h2>
      </div>

      <Card>
        <div className="flex gap-3 mb-4">
          <Input
            placeholder="Öğrenci veya veli adına göre ara..."
            allowClear
            style={{ width: 300 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select
            placeholder="Teslim durumu"
            allowClear
            style={{ width: 160 }}
            value={deliveredFilter}
            onChange={(value) => {
              setDeliveredFilter(value ?? null);
              fetchCertificates(1, debouncedSearch, value ?? null);
            }}
          >
            <Option value="no">Bekliyor</Option>
            <Option value="yes">Teslim Edildi</Option>
          </Select>
          <Select
            placeholder="Basım durumu"
            allowClear
            style={{ width: 210 }}
            value={statusFilter}
            onChange={(value) => setStatusFilter(value ?? null)}
          >
            <Option value="not_printed">Basılmadı</Option>
            <Option value="printed_not_delivered">Basıldı, teslim edilmedi</Option>
          </Select>
          <RangePicker
            placeholder={['Oluşturulma (başlangıç)', 'Oluşturulma (bitiş)']}
            format="DD.MM.YYYY"
            value={dateRange}
            onChange={(range) => {
              setDateRange(range);
              fetchCertificates(1, debouncedSearch, deliveredFilter, pagination.pageSize, sortField, sortOrder ? (sortOrder === 'descend' ? 'desc' : 'asc') : null, range);
            }}
          />
        </div>

        <Table
          columns={columns}
          dataSource={certificates}
          rowKey="id"
          loading={loading}
          onChange={(paginationInfo, _filters, sorter) => {
            const { sortField: sField, sortDirection: sDir } = applySorter(sorter);
            setPersistedPageSize(paginationInfo.pageSize);
            fetchCertificates(paginationInfo.current, debouncedSearch, deliveredFilter, paginationInfo.pageSize, sField, sDir);
          }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100'],
            showTotal: (total) => `Toplam ${total} sertifika`,
          }}
        />
      </Card>
    </div>
  );
};

export default CertificateDeliveries;
